import type { Attachment } from "@theta/api-client";
import type { Attachment as TrayAttachment } from "@/lib/postRaw";

/**
 * ONE `attachments` ARRAY, WITH RENDITIONS
 *
 * The old serializer sent `image_urls` AND `image_grid_urls` as two lists, and
 * the two could disagree about how many images a post had — a post with three
 * images and two grid variants rendered a gallery with a hole in it. v1 sends
 * one ordered `attachments` array and the grid is a RENDITION of each entry
 * (C16/U11), so the question cannot be asked twice and answered differently.
 */

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE ONES THERE ARE ACTUALLY BYTES BEHIND — OSO-490 slice 3
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `Attachment.url` is `string | null`: null while a video is `processing`, and
 * null forever if the transcode `failed`. Such an attachment reaches only its
 * AUTHOR — on a row that is still waiting nobody else sees the row at all, and
 * on a live row the server omits the attachment — so this filter is about what
 * the author's own client draws, not about who may see what.
 *
 * Until slice 4 gives them a placeholder and a "Fjern video" to act on, every
 * renderer shows the ready ones and nothing else. THIS IS THE ONE PLACE THAT
 * SAYS SO: slice 4 replaces the call at each site rather than hunting for
 * `url ?? ""` fallbacks, which is what the alternative would have left behind
 * — and an empty src is a broken-image glyph, not an honest "not yet".
 *
 * GENERIC over the element, like `mediaAttachments`: the gallery's
 * `GroupAttachment` carries `postId`/`author` its sidebar needs.
 */
export type Ready<T> = T & { url: string };

export function readyAttachments<T extends Pick<Attachment, "status" | "url">>(
  list: readonly T[],
): Ready<T>[] {
  return list.filter((a): a is Ready<T> => a.status === "ready" && a.url !== null);
}

/**
 * The raster images, in display order.
 *
 * Callers read `a.renditions?.grid ?? a.url` for a grid cell, and the `?.` is
 * load-bearing: `renditions` IS NULL for anything that is not a raster image —
 * a video, an audio file, a 40 MB PDF scan — and reading `.thumb` off one
 * without checking is how a thumbnail grid downloads the original.
 *
 * A media url is also read AT RENDER TIME and never stored. Protected-tier
 * urls are signed and hour-rounded: byte-identical inside the window, 403
 * outside it, so a copy in localStorage, a service-worker cache, a `useState`
 * that outlives the window or a React `key` is a copy that breaks later.
 */
export function imageAttachments(attachments: Attachment[]): Attachment[] {
  return attachments.filter((a) => a.kind === "image");
}

/**
 * The videos, in display order.
 *
 * A video has no renditions to build a grid cell from — its still comes from
 * `posterUrl`, written by the transcode job. It is played by `VideoPlayer`,
 * which never autoplays (OSO-437). Same read-at-render-time rule as above; the
 * url may be signed.
 */
export function videoAttachments(attachments: Attachment[]): Attachment[] {
  return attachments.filter((a) => a.kind === "video");
}

/**
 * Everything `ImageLightbox` can show, in display order: images and videos.
 *
 * ONE LIST, ONE SET OF INDICES. The lightbox used to take image urls only, so
 * every caller kept a second index that skipped the videos — and a clip sitting
 * between two photos made the two disagree, which is how the gallery opened the
 * wrong picture (OSO-477). A caller that derives both the tiles and the
 * lightbox from this cannot reintroduce that.
 *
 * Anything else an attachment can be — audio, a PDF — stays out: the lightbox
 * has nothing to render it with, and `FileChip` already does that job in place.
 *
 * GENERIC over the element, unlike its two neighbours, because the gallery's
 * `GroupAttachment` carries `postId`/`author` that its sidebar needs. Widening
 * to `Attachment` here would strip them and make the caller cast them back.
 */
export function mediaAttachments<T extends Pick<Attachment, "kind">>(attachments: T[]): T[] {
  return attachments.filter((a) => a.kind === "image" || a.kind === "video");
}

/**
 * EVERYTHING THE GALLERY AND THE PLAYERS DO NOT DRAW: audio and files, in
 * display order — and the reason a post or a comment can carry one at all.
 *
 * `imageAttachments` and `videoAttachments` were the only two lists `PostCard`
 * and `CommentItem` read, so an `audio` or a `file` row reached the server,
 * was stored, was serialized and was then rendered by nobody: an MP3 attached
 * in the composer sat in the tray, survived the submit, and vanished from its
 * own post with no error anywhere. `MessageBubble` had drawn both kinds since
 * §1.12, which is why the bug read as "works in beskeder, not in a group".
 *
 * ONE LIST RATHER THAN AN `audio` ONE AND A `file` ONE, because `position`
 * orders them together: a voice memo posted between two PDFs belongs between
 * them. `AttachedFiles` picks the player or the chip per row.
 */
export function fileAttachments<T extends Pick<Attachment, "kind">>(attachments: T[]): T[] {
  return attachments.filter((a) => a.kind === "audio" || a.kind === "file");
}

/**
 * THE TRAY'S ROWS, IN THE SHAPE A COMMENT CARRIES — for an optimistic row only.
 *
 * A freshly sent comment is drawn from a placeholder until the server answers
 * (OSO-543). The tray knows the url, the kind and the renditions of what it
 * uploaded, which is everything the gallery reads; what it does NOT know is
 * filled in honestly rather than guessed — `filesize` 0, `contentType` and the
 * dimensions `null`, a negative placeholder `id` like the comment's own. None
 * of it is read before `onSuccess` swaps the whole row for the server's, which
 * carries the real values. Rows still uploading are left out, as the write
 * path leaves them out of the request.
 */
/**
 * THE OTHER HALF OF THE OPTIMISTIC ROW (OSO-490): is it published yet?
 *
 * The composer no longer waits for a transcode, so a comment can be sent with
 * a `processing` video on it — and the server will hand that row back with
 * `publishedAt: null`, which is what draws the muted card. An optimistic row
 * that guessed "published" would be undimmed and interactive for the length of
 * the round trip and then flip, which is the same picture-changing-under-the
 * -reader that `optimisticAttachments` carrying the real status exists to
 * prevent. So the placeholder states what the server is about to say.
 *
 * `processing` and not `!== "ready"`: a `failed` upload never reaches a
 * submitted row — `useAttachments` refuses it at the 201 — so the only reason
 * a fresh row is held back is a clip still encoding.
 */
export function optimisticPublishedAt(tray: TrayAttachment[]): string | null {
  return tray.some((a) => a.status === "processing")
    ? null
    : new Date().toISOString();
}

/**
 * THE TRAY HAS NO `audio` KIND, AND THE SERVER DOES.
 *
 * `kindForFile` answers `image | video | file` off `File.type`, so a voice
 * memo sits in the tray as a `file` — that is deliberate, the tray draws it as
 * a chip either way. But the server derives the kind from the upload ROW
 * (`attachmentKindFor`) and answers `audio`, so an optimistic comment that
 * passed the tray's word through drew a file chip for a second and then
 * swapped it for a player when the real row landed. That is the same picture
 * -changing-under-the-reader that the `status` note above exists to prevent,
 * so the placeholder states what the server is about to say — from the
 * filename, which is what the server's extension is taken from too.
 */
const AUDIO_FILENAME = /\.(mp3|wav|ogg|oga|m4a|opus|flac|aac)$/i;

export function optimisticAttachments(tray: TrayAttachment[]): Attachment[] {
  return tray
    .filter((a) => a.uploadId != null && !a.uploading)
    .map((a, index) => ({
      id: -(index + 1) as Attachment["id"],
      uploadId: a.uploadId as Attachment["uploadId"],
      kind: a.kind === "file" && AUDIO_FILENAME.test(a.name) ? "audio" : a.kind,
      position: index,
      // THE TRAY'S OWN ANSWER, not `"ready"` (OSO-490). The composer no longer
      // waits for a transcode, so a just-sent comment can carry a video the
      // worker is still encoding — and the optimistic card has to draw the
      // same "behandles" placeholder the server's row will, or the picture
      // changes under the reader when the real comment arrives.
      status: a.status,
      url: a.url,
      renditions: a.renditions ?? null,
      posterUrl: null,
      // An optimistic tray attachment is on the LOCAL path (it was just
      // uploaded through `POST /uploads`); a Bunny video never enters the tray,
      // so there is no HLS source to advertise here (OSO-645).
      hlsUrl: null,
      filename: a.name,
      contentType: null,
      filesize: 0,
      width: null,
      height: null,
    }));
}
